// Top-level app: routing, theme, kiosk behaviour and global hosts.

import { lazy, Suspense, useEffect } from 'react';
import { useDevice, useHousehold, useSyncState } from '../data/hooks.ts';
import { EventEditorHost } from '../features/log/EventEditor.tsx';
import { VetVisitHost } from '../features/health/VetVisits.tsx';
import { AppointmentHost } from '../features/health/Appointments.tsx';
import { VoiceHost } from '../features/voice/VoiceAssistant.tsx';
import { Today } from '../features/today/Today.tsx';
import { ToastHost } from '../ui/toast.tsx';
import { useKiosk } from './kiosk.ts';
import { useServerBuild } from './build.ts';
import { startAwayTracking } from '../data/away.ts';

startAwayTracking();
import { navigate, useRoute } from './router.ts';
import { Shell } from './Shell.tsx';

const Timeline = lazy(() => import('../features/timeline/Timeline.tsx'));
const Walks = lazy(() => import('../features/walks/Walks.tsx'));
const Ask = lazy(() => import('../features/ask/Ask.tsx'));
const Camera = lazy(() => import('../features/camera/Camera.tsx'));
const Insights = lazy(() => import('../features/insights/Insights.tsx'));
const Health = lazy(() => import('../features/health/Health.tsx'));
const Food = lazy(() => import('../features/food/Food.tsx'));
const Training = lazy(() => import('../features/training/Training.tsx'));
const Social = lazy(() => import('../features/social/Social.tsx'));
const Guide = lazy(() => import('../features/guide/Guide.tsx'));
const Shopping = lazy(() => import('../features/shop/Shopping.tsx'));
const Costs = lazy(() => import('../features/costs/Costs.tsx'));
const Emergency = lazy(() => import('../features/emergency/Emergency.tsx'));
const LostDog = lazy(() => import('../features/emergency/LostDog.tsx'));
const Diary = lazy(() => import('../features/diary/Diary.tsx'));
const VetReport = lazy(() => import('../features/health/VetReport.tsx'));
const Homecoming = lazy(() => import('../features/homecoming/Homecoming.tsx'));
const Sitter = lazy(() => import('../features/sitter/Sitter.tsx'));
const Settings = lazy(() => import('../features/settings/Settings.tsx'));
const Setup = lazy(() => import('../features/setup/Setup.tsx'));
const Tap = lazy(() => import('../features/tap/Tap.tsx'));
const NightClock = lazy(() => import('../features/night/NightClock.tsx'));
const Ambient = lazy(() => import('../features/night/Ambient.tsx'));

export function App() {
  const route = useRoute();
  const household = useHousehold();
  const device = useDevice();
  const kiosk = useKiosk();
  const sync = useSyncState();

  // The wall tablet never reloads by itself — so after a deploy it picks up
  // the new version the next time nobody's using it (screensaver or night clock;
  // not while the screen is off, as reloading would switch it back on).
  const { stale } = useServerBuild();
  useEffect(() => {
    if (stale && device.kiosk && (kiosk.ambient || kiosk.dimmed)) location.reload();
  }, [stale, device.kiosk, kiosk.ambient, kiosk.dimmed]);

  // Theme: explicit choice, or dark during the household's night hours.
  useEffect(() => {
    const root = document.documentElement;
    const theme = device.theme === 'auto' ? (kiosk.isNight ? 'dark' : undefined) : device.theme;
    if (theme) root.dataset.theme = theme;
    else delete root.dataset.theme;
  }, [device.theme, kiosk.isNight]);

  // First run: onboarding — but give a new device the chance to pull an
  // existing household from the server first.
  const waiting = sync.kind === 'connecting';
  useEffect(() => {
    if (!waiting && !household.onboarded && !device.guest && route.segments[0] !== 'setup' && route.segments[0] !== 'tap') navigate('/setup', { replace: true });
  }, [waiting, household.onboarded, route.segments, device.guest]);

  let [section, sub]: (string | undefined)[] = route.segments;
  // Sitters get the sitter guide instead of setup and settings.
  if (device.guest && (section === 'setup' || section === 'settings' || section === 'vet-report')) [section, sub] = ['sitter', undefined];

  if (section === 'setup')
    return (
      <Suspense fallback={<Loading />}>
        <Setup />
      </Suspense>
    );

  let page;
  switch (section) {
    case undefined:
      page = <Today />;
      break;
    case 'timeline':
      page = <Timeline />;
      break;
    case 'walks':
      page = <Walks />;
      break;
    case 'ask':
      page = <Ask />;
      break;
    case 'camera':
      page = <Camera />;
      break;
    case 'insights':
      page = <Insights />;
      break;
    case 'diary':
      page = <Diary />;
      break;
    case 'sitter':
      page = <Sitter />;
      break;
    case 'homecoming':
      page = <Homecoming />;
      break;
    case 'vet-report':
      page = <VetReport />;
      break;
    case 'health':
      page = <Health tab={sub} />;
      break;
    case 'food':
      page = <Food />;
      break;
    case 'training':
      page = <Training sub={sub} />;
      break;
    case 'social':
      page = <Social />;
      break;
    case 'guide':
      page = <Guide id={sub} />;
      break;
    case 'shopping':
      page = <Shopping />;
      break;
    case 'costs':
      page = <Costs />;
      break;
    case 'emergency':
      page = <Emergency />;
      break;
    case 'lost':
      page = <LostDog />;
      break;
    case 'settings':
      page = <Settings tab={sub} />;
      break;
    case 'tap':
      page = <Tap action={sub} />;
      break;
    default:
      page = <Today />;
  }

  return (
    <>
      <Shell>
        <Suspense fallback={<Loading />}>{page}</Suspense>
      </Shell>
      <EventEditorHost />
      <VetVisitHost />
      <AppointmentHost />
      <VoiceHost />
      <ToastHost />
      {kiosk.dimmed && (
        <Suspense fallback={null}>
          <NightClock onWake={kiosk.wake} />
        </Suspense>
      )}
      {kiosk.ambient && (
        <Suspense fallback={null}>
          <Ambient onWake={kiosk.wake} />
        </Suspense>
      )}
      {kiosk.off && <ScreenOff onWake={kiosk.wake} />}
    </>
  );
}

/** The screen "turned off": pure black (no light from the pixels), until a tap. */
function ScreenOff({ onWake }: { onWake: () => void }) {
  return (
    <button type="button" className="screen-off" onClick={onWake} aria-label="Turn the screen back on">
      <span className="screen-off-hint">Tap to turn on</span>
    </button>
  );
}

function Loading() {
  return (
    <div className="loading" aria-busy="true">
      <span className="loading-dot" />
    </div>
  );
}
